// Phase 2 Round 3 extraction (verbatim).
import React from 'react';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import {calcCattleBreedingTimeline, buildCattleCycleSeqMap, cattleCycleLabel} from '../lib/cattleBreeding.js';
import {upsertCattleBreedingCycle, deleteCattleBreedingCycle} from '../lib/cattleBreedingCycleApi.js';
import {getProgramColor} from '../lib/programColors.js';
import {getReadableText} from '../lib/styles.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {canWriteLivestockRecords, type AuthState as AccessAuthState} from '../lib/access.js';
import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleBreedingCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';

type CattleRecord = Record<string, any>;
type Notice = {kind?: 'error' | 'warning' | 'success' | 'info'; message?: string | null};
type CycleForm = {herd: string; bull_exposure_start: string; bull_tags: string; cow_tags: string; notes: string};
type BreedingStatus = 'planned' | 'exposure' | 'pregcheck' | 'calving' | 'nursing' | 'complete';

// Module scope, so the params object identity is stable and a re-render cannot
// look like a key change.
const CATTLE_BREEDING_PARAMS = Object.freeze({});
// Frozen and shared so an empty render never allocates a new array identity.
const EMPTY_BREEDING_ROWS = Object.freeze([]) as readonly CattleRecord[] as CattleRecord[];

const CattleBreedingView = () => {
  const {authState} = useAuth();
  // Migration 221: upsert/delete_cattle_breeding_cycle admit admin, management
  // and farm_team only. Other roles (Equipment Tech) read the cycles; the Add
  // and Edit controls -- the only way into the form's Save and Delete -- are
  // hidden.
  const canWrite = canWriteLivestockRecords(authState as AccessAuthState);
  const {setView} = useUI();
  const {useState, useEffect} = React;
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<CycleForm | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  // One scoped read owns all three lists. The page-local loader it replaced
  // wrote `if (cR.data) setCycles(cR.data)` and then cleared loading
  // unconditionally, so a FAILED read rendered the "No breeding cycles yet"
  // empty state — inviting the operator to create a cycle that may already
  // exist. The resource throws instead, and the error branch below is what the
  // operator now sees.
  //
  // Query keeps the previous rows on screen during a same-key refetch, so the
  // `await loadAll()` after save/delete no longer flashes the list to empty.
  const breeding = useRouteResource(RESOURCE.CATTLE_BREEDING_CORE, CATTLE_BREEDING_PARAMS);
  const breedingData = breeding.data as CattleBreedingCoreData | null;
  const cycles = breedingData ? (breedingData.cycles as CattleRecord[]) : EMPTY_BREEDING_ROWS;
  const calving = breedingData ? (breedingData.calvingRecords as CattleRecord[]) : EMPTY_BREEDING_ROWS;
  const cattle = breedingData ? (breedingData.cattle as CattleRecord[]) : EMPTY_BREEDING_ROWS;
  const loading = breeding.loading;
  const loadAll = breeding.refetch;
  const loadError: Notice | null = breeding.error
    ? {
        kind: 'error',
        message:
          'Could not load breeding cycles. Please refresh the page. (' +
          (breeding.error instanceof Error ? breeding.error.message : String(breeding.error)) +
          ')',
      }
    : null;

  const seqMap = buildCattleCycleSeqMap(cycles);

  function openAdd() {
    setNotice(null);
    setForm({
      herd: 'mommas',
      bull_exposure_start: new Date().toISOString().slice(0, 10),
      bull_tags: '',
      cow_tags: '',
      notes: '',
    });
    setEditId(null);
    setShowForm(true);
  }
  function openEdit(c: CattleRecord) {
    setNotice(null);
    setForm({
      herd: c.herd || 'mommas',
      bull_exposure_start: c.bull_exposure_start,
      bull_tags: c.bull_tags || '',
      cow_tags: c.cow_tags || '',
      notes: c.notes || '',
    });
    setEditId(c.id);
    setShowForm(true);
  }

  async function saveCycle() {
    if (!form) return;
    setNotice(null);
    if (!form.bull_exposure_start) {
      setNotice({kind: 'error', message: 'Bull exposure start date is required.'});
      return;
    }
    const rec = {
      herd: form.herd,
      bull_exposure_start: form.bull_exposure_start,
      bull_tags: form.bull_tags || null,
      cow_tags: form.cow_tags || null,
      notes: form.notes || null,
    };
    try {
      await upsertCattleBreedingCycle(sb, {id: editId || null, ...rec});
      await loadAll();
      setShowForm(false);
      setEditId(null);
      setForm(null);
    } catch (e) {
      const error = e as CattleRecord;
      setNotice({kind: 'error', message: 'Save failed: ' + (error.message || String(e))});
    }
  }
  async function deleteCycle(id: string) {
    if (!window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete this breeding cycle?', async () => {
      setNotice(null);
      try {
        await deleteCattleBreedingCycle(sb, id);
        await loadAll();
        setShowForm(false);
        setEditId(null);
        setForm(null);
      } catch (e) {
        const error = e as CattleRecord;
        setNotice({kind: 'error', message: 'Delete failed: ' + (error.message || String(e))});
      }
    });
  }

  function cycleStatus(c: CattleRecord, tl: CattleRecord | null): BreedingStatus {
    if (!tl) return 'planned';
    const today = new Date().toISOString().slice(0, 10);
    if (today < c.bull_exposure_start) return 'planned';
    if (today <= tl.exposureEnd) return 'exposure';
    if (today < tl.calvingStart) return 'pregcheck';
    if (today <= tl.calvingEnd) return 'calving';
    if (today <= tl.weaningDate) return 'nursing';
    return 'complete';
  }
  // Lifecycle status → Badge variant (WI-4). planned = upcoming (warn);
  // the in-progress phases (exposure/pregcheck/calving/nursing) read as info;
  // complete = ok.
  const STATUS_VARIANTS: Record<BreedingStatus, 'warn' | 'info' | 'ok'> = {
    planned: 'warn',
    exposure: 'info',
    pregcheck: 'info',
    calving: 'info',
    nursing: 'info',
    complete: 'ok',
  };

  function outstandingCows(c: CattleRecord) {
    const expectedTags = (c.cow_tags || '')
      .split(/[\n,]+/)
      .map((t: string) => t.trim())
      .filter(Boolean);
    if (expectedTags.length === 0) return [];
    const farrowed = new Set(
      calving
        .filter(
          (r: CattleRecord) =>
            r.cycle_id === c.id ||
            (r.dam_tag &&
              expectedTags.includes(r.dam_tag) &&
              r.calving_date &&
              c.bull_exposure_start &&
              new Date(r.calving_date) >= new Date(c.bull_exposure_start)),
        )
        .map((r: CattleRecord) => r.dam_tag),
    );
    return expectedTags.filter((t: string) => !farrowed.has(t));
  }

  const inpS: React.CSSProperties = {
    fontSize: 13,
    padding: '7px 10px',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  };
  const lbl = {fontSize: 11, color: 'var(--ink-muted)', display: 'block', marginBottom: 3, fontWeight: 500};

  return (
    <div style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
      <Header />
      <div style={{padding: '1rem', maxWidth: 1100, margin: '0 auto'}}>
        {!showForm && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
          <div style={{fontSize: 16, fontWeight: 700, color: 'var(--ink)'}}>
            Breeding Cycles{' '}
            <span style={{fontSize: 13, fontWeight: 400, color: 'var(--ink-muted)'}}>({cycles.length})</span>
          </div>
          {canWrite && (
            <button
              onClick={openAdd}
              style={{
                padding: '7px 16px',
                borderRadius: 10,
                border: 'none',
                background: getProgramColor('cattle'),
                color: getReadableText(getProgramColor('cattle')),
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + New Cycle
            </button>
          )}
        </div>

        {loading && (
          <div style={{textAlign: 'center', padding: '2rem', color: 'var(--ink-faint)'}}>Loading{'\u2026'}</div>
        )}
        {/* Gated AHEAD of the empty state and mutually exclusive with it. A
            failed read used to fall through to "No breeding cycles yet", which
            reads as "the farm has none" and invites a duplicate cycle. */}
        {!loading && loadError && (
          <div data-cattle-breeding-load-error="1" style={{padding: '1rem 0'}}>
            <InlineNotice notice={loadError} />
            <button
              type="button"
              data-cattle-breeding-load-retry="1"
              onClick={() => loadAll()}
              style={{
                marginTop: 8,
                padding: '6px 12px',
                border: '1px solid var(--border)',
                borderRadius: 10,
                background: 'var(--surface)',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        )}
        {!loading && !loadError && cycles.length === 0 && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '2rem',
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            No breeding cycles yet. Click <strong>+ New Cycle</strong> to record one.
          </div>
        )}

        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          {cycles.map((c) => {
            const tl = calcCattleBreedingTimeline(c.bull_exposure_start);
            const status = cycleStatus(c, tl);
            const outstanding = outstandingCows(c);
            const cowList = (c.cow_tags || '')
              .split(/[\n,]+/)
              .map((t: string) => t.trim())
              .filter(Boolean);
            const bullList = (c.bull_tags || '')
              .split(/[\n,]+/)
              .map((t: string) => t.trim())
              .filter(Boolean);
            return (
              <div
                key={c.id}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '14px 18px',
                }}
              >
                <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap'}}>
                  <span style={{fontSize: 14, fontWeight: 700, color: 'var(--ink)'}}>
                    {cattleCycleLabel(c, seqMap)}
                  </span>
                  <Badge variant={STATUS_VARIANTS[status] || 'neutral'} style={{textTransform: 'uppercase'}}>
                    {status}
                  </Badge>
                  <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                    {cowList.length} {cowList.length === 1 ? 'cow' : 'cows'}{' '}
                    {bullList.length > 0 ? '\u00b7 bulls: ' + bullList.join(', ') : ''}
                  </span>
                  {canWrite && (
                    <button
                      onClick={() => openEdit(c)}
                      style={{
                        marginLeft: 'auto',
                        fontSize: 11,
                        color: 'var(--brand)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
                {tl && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: 8,
                      fontSize: 11,
                      color: 'var(--ink-muted)',
                    }}
                  >
                    <div>
                      <div style={{color: 'var(--ink-faint)', fontSize: 10, textTransform: 'uppercase'}}>
                        Bull Exposure
                      </div>
                      <div style={{fontWeight: 600}}>
                        {fmt(c.bull_exposure_start)} {'\u2014'} {fmt(tl.exposureEnd)}
                      </div>
                    </div>
                    <div>
                      <div style={{color: 'var(--ink-faint)', fontSize: 10, textTransform: 'uppercase'}}>
                        Preg Check
                      </div>
                      <div style={{fontWeight: 600}}>{fmt(tl.pregCheckDate)}</div>
                    </div>
                    <div>
                      <div style={{color: 'var(--ink-faint)', fontSize: 10, textTransform: 'uppercase'}}>
                        Calving Window
                      </div>
                      <div style={{fontWeight: 600}}>
                        {fmt(tl.calvingStart)} {'\u2014'} {fmt(tl.calvingEnd)}
                      </div>
                    </div>
                    <div>
                      <div style={{color: 'var(--ink-faint)', fontSize: 10, textTransform: 'uppercase'}}>Wean By</div>
                      <div style={{fontWeight: 600}}>{fmt(tl.weaningDate)}</div>
                    </div>
                  </div>
                )}
                {outstanding.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: '8px 12px',
                      background: 'var(--warn-soft)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                    }}
                  >
                    <div style={{fontSize: 11, fontWeight: 700, color: 'var(--warn-ink)', marginBottom: 4}}>
                      {'\u26a0 Outstanding cows (' + outstanding.length + ')'}
                    </div>
                    <div style={{fontSize: 11, color: 'var(--warn-ink)'}}>
                      {outstanding.map((t: string) => '#' + t).join(', ')}
                    </div>
                  </div>
                )}
                {c.notes && (
                  <div style={{marginTop: 8, fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic'}}>
                    {c.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* cattle.breeding workflow Activity stream (entity_id cattle-breeding),
            populated by the audited upsert/delete cycle RPCs (mig 094). Audit
            only — comments are not a breeding-workflow surface. */}
        <div data-cattle-breeding-activity style={{marginTop: 16}}>
          <RecordCollaborationSection
            sb={sb}
            authState={authState}
            entityType="cattle.breeding"
            entityId="cattle-breeding"
            entityLabel="Cattle Breeding"
            activityLimit={100}
            showComments={false}
          />
        </div>
      </div>

      {/* Cycle form modal */}
      {showForm && form && (
        <div
          onClick={() => {
            setNotice(null);
            setShowForm(false);
            setEditId(null);
            setForm(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,.45)',
            zIndex: 500,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '1rem',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: 12,
              width: '100%',
              maxWidth: 520,
              boxShadow: '0 8px 32px rgba(0,0,0,.2)',
              marginTop: 40,
            }}
          >
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{fontSize: 15, fontWeight: 600, color: 'var(--text-primary)'}}>
                {editId ? 'Edit Cycle' : 'New Breeding Cycle'}
              </div>
              <button
                onClick={() => {
                  setNotice(null);
                  setShowForm(false);
                  setEditId(null);
                  setForm(null);
                }}
                style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-faint)'}}
              >
                {'\u00d7'}
              </button>
            </div>
            <div style={{padding: '16px 20px'}}>
              <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
              <div style={{marginBottom: 10}}>
                <label style={lbl}>Bull Exposure Start *</label>
                <input
                  type="date"
                  value={form.bull_exposure_start}
                  onChange={(e) => setForm({...form, bull_exposure_start: e.target.value})}
                  style={inpS}
                />
                <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 3}}>
                  All other dates auto-compute: 65d exposure {'\u2192'} 30d preg check {'\u2192'} 9mo gestation{' '}
                  {'\u2192'} 65d calving {'\u2192'} 7mo nursing.
                </div>
              </div>
              <div style={{marginBottom: 10}}>
                <label style={lbl}>Bull tag(s)</label>
                <input
                  value={form.bull_tags}
                  onChange={(e) => setForm({...form, bull_tags: e.target.value})}
                  placeholder="comma or newline separated"
                  style={inpS}
                />
              </div>
              <div style={{marginBottom: 10}}>
                <label style={lbl}>Cow tags in cycle</label>
                <textarea
                  value={form.cow_tags}
                  onChange={(e) => setForm({...form, cow_tags: e.target.value})}
                  placeholder="One per line or comma-separated"
                  rows={5}
                  style={{...inpS, fontFamily: 'monospace'}}
                />
                <div style={{fontSize: 11, color: 'var(--text-primary)', marginTop: 3}}>
                  {
                    (form.cow_tags || '')
                      .split(/[\n,]+/)
                      .map((t) => t.trim())
                      .filter(Boolean).length
                  }{' '}
                  cows
                </div>
              </div>
              <div style={{marginBottom: 10}}>
                <label style={lbl}>Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  rows={2}
                  style={{...inpS, resize: 'vertical'}}
                />
              </div>
            </div>
            <div style={{padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8}}>
              <button
                onClick={saveCycle}
                style={{
                  padding: '8px 20px',
                  borderRadius: 10,
                  border: 'none',
                  background: getProgramColor('cattle'),
                  color: getReadableText(getProgramColor('cattle')),
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Save
              </button>
              {editId && (
                <button
                  onClick={() => deleteCycle(editId)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 10,
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--danger-ink)',
                    fontSize: 13,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => {
                  setNotice(null);
                  setShowForm(false);
                  setEditId(null);
                  setForm(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--border-strong)',
                  background: 'var(--surface)',
                  color: 'var(--ink-muted)',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CattleBreedingView;
